import { Component, DestroyRef, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Profile } from '@shared/models/profile.model';
import { Role } from '@shared/models/role.model';
import { ErrorService } from '@shared/services/error.service';
import { IbodeGraphsService } from '@shared/services/ibode-graphs.service';
import { SubscriptionManager } from '@shared/services/subscription-manager.service';
import { isForbiddenSpecialty } from '@shared/utils/duplication.checker';
import { InstanceIdService } from '@shared/services/instance-id.service';

@Component({
  selector: 'app-popup-role-skill-details',
  templateUrl: './popup-role-skill-details.component.html',
  styleUrls: ['./popup-role-skill-details.component.scss'],
  standalone: false,
})
export class PopupRoleSkillDetailsComponent implements OnInit {
  private instanceId: string;

  public paramedicals: any = [];

  public roles: Role[] | undefined = [];

  public role: Role | undefined = undefined;

  public profile: Profile | undefined = undefined;

  public selectIndiv: Role | undefined = undefined;

  public experienceIndiv: any[] = [];

  public experienceIndivLoader: boolean = true;

  public nurseId: string = '';

  public chartIndex: number = 0;

  public secondChartIndex: number = 0;

  constructor(
    public dialogRef: MatDialogRef<PopupRoleSkillDetailsComponent>,
    public ibodeGraphsService: IbodeGraphsService,
    public errorService: ErrorService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private destroyRef: DestroyRef,
    private subscriptionManager: SubscriptionManager,
    private instanceIdService: InstanceIdService
  ) {
    this.instanceId = this.instanceIdService.generateId();
    this.subscriptionManager.registerComponent(this.instanceId, this.destroyRef);
  }

  ngOnInit(): void {
    if (this.data) {
      this.roles = this.data.roles;
      this.role = this.data.role;
      this.selectIndiv = this.data.roles?.filter((role: Role) => role._id !== this.data.role?._id)[0];
      this.profile = this.data.profile;
      this.paramedicals = this.data.paramedicals;
    }
    if (this.profile && this.profile._id) {
      this.nurseId = this.profile._id;
    }

    this.getSpecialtiesProgress();
  }

  onRoleChange(event: Role) {
    this.role = event;
    this.chartIndex = this.experienceIndiv?.findIndex((exp: any) => exp.role.name === this.role?.name) || 0;
  }

  onSecondRoleChange(event: Role) {
    this.selectIndiv = event;
    this.secondChartIndex =
      this.experienceIndiv?.findIndex((exp: any) => exp.role.name === this.selectIndiv?.name) || 0;
  }

  compareRole(a: Role, b: Role) {
    if ((a === null || a === undefined) && (b === null || b === undefined)) {
      return true;
    }
    if (a && b) {
      return String(a._id) === String(b._id);
    } else {
      return false;
    }
  }

  getSpecialtiesProgress() {
    this.subscriptionManager
      .createRequest(
        this.ibodeGraphsService.getSpecialtiesProgress(this.nurseId!),
        'getSpecialtiesProgress',
        this.instanceId
      )
      .subscribe({
        next: (results: any) => {
          this.experienceIndiv = results;
          this.experienceIndiv.forEach((role: any) => {
            role.averageExperienceProgress = this.calculateProgressOfAverageExperience(role.roleData);
          });
          this.experienceIndiv = this.sortRoleByPriorityAndName(this.experienceIndiv, 1);
          this.roles = this.experienceIndiv.map((exp: any) => exp.role);
          this.selectIndiv = this.roles.filter((role: Role) => role._id !== this.role?._id)[0];
          this.chartIndex = this.experienceIndiv?.findIndex((exp: any) => exp.role._id === this.role?._id);
          this.secondChartIndex = this.experienceIndiv?.findIndex((exp: any) => exp.role._id === this.selectIndiv?._id);
          this.experienceIndivLoader = false;
        },
        error: (error: any) => this.errorService.handleError(error),
      });
  }

  calculateProgressOfAverageExperience(array: any[]): number {
    const specialties = array.filter((item: any) => !isForbiddenSpecialty(item.specialty.name));
    const averageLevel = this.getAverageLevel(specialties);
    const maxLevel = this.getMaxLevel(specialties);
    return parseInt(((averageLevel / maxLevel) * 100).toFixed(0));
  }

  getMaxLevel(data: any[]): number {
    let maxLevel = 0;
    for (const item of data) {
      if (maxLevel < item.maxLevel) {
        maxLevel = item.maxLevel;
      }
    }
    return maxLevel;
  }

  getAverageLevel(data: any[]): number {
    let levelsSum = 0;
    let zeroCount = 0;
    for (const item of data) {
      if (item.level === 0) {
        zeroCount = zeroCount + 1;
      }
    }
    for (const item of data) {
      levelsSum = levelsSum + item.level;
    }
    const count = data.length - zeroCount ? data.length - zeroCount : 1;
    return levelsSum / count;
  }

  sortRoleByPriorityAndName(data: any, mode: any) {
    const rolesPriority = this.paramedicals?.find((obj: any) => obj.formation.name === this.profile?.position);
    const priorityMap = rolesPriority
      ? new Map(rolesPriority.roles.map((rp: any) => [rp.roleId._id, rp.priority]))
      : new Map();
    let result;

    if (mode === 0) {
      let rolesWithPriority = data.filter((role: any) => priorityMap.has(role._id) && priorityMap.get(role._id) !== 0);
      rolesWithPriority = rolesWithPriority.sort(function (a: any, b: any) {
        const priorityA = priorityMap.get(a._id) || 0;
        const priorityB = priorityMap.get(b._id) || 0;
        if (priorityA === priorityB) {
          if (a.name < b.name) {
            return -1;
          }
          if (a.name > b.name) {
            return 1;
          }
          return 0;
        } else {
          return (priorityA as number) - (priorityB as number);
        }
      });

      let rolesWithoutPriority = data.filter(
        (role: any) => !priorityMap.has(role._id) || priorityMap.get(role._id) === 0
      );
      rolesWithoutPriority = rolesWithoutPriority.sort(function (a: any, b: any) {
        if (a.name < b.name) {
          return -1;
        }
        if (a.name > b.name) {
          return 1;
        }
        return 0;
      });

      result = [...rolesWithPriority, ...rolesWithoutPriority];
    } else {
      let rolesWithPriority = data?.filter(
        (data: any) => data.role && priorityMap.has(data.role._id) && priorityMap.get(data.role._id) !== 0
      );
      rolesWithPriority = rolesWithPriority.sort(function (a: any, b: any) {
        const priorityA = priorityMap.get(a.role._id) || 0;
        const priorityB = priorityMap.get(b.role._id) || 0;
        if (priorityA === priorityB) {
          if (a.role.name < b.role.name) {
            return -1;
          }
          if (a.role.name > b.role.name) {
            return 1;
          }
          return 0;
        } else {
          return (priorityA as number) - (priorityB as number);
        }
      });
      let rolesWithoutPriority = data.filter(
        (data: any) => !data.role || !priorityMap.has(data.role._id) || priorityMap.get(data.role._id) === 0
      );
      rolesWithoutPriority = rolesWithoutPriority.sort(function (a: any, b: any) {
        if (a.role && b.role) {
          if (a.role.name < b.role.name) {
            return -1;
          }
          if (a.role.name > b.role.name) {
            return 1;
          }
        }
        return 0;
      });
      result = [...rolesWithPriority, ...rolesWithoutPriority];
    }
    return result;
  }

  redirectToIndividualSkills() {
    window.open(
      window.location.origin + '/parcours-ibode/competence/competence-individuelle/' + this.nurseId,
      '_blank'
    );
  }

  closePopup(): void {
    this.dialogRef.close();
  }
}
